// DEPENDENCIES
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import moment from 'moment';
// COMPONENTS
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import MUIDataTable from 'mui-datatables';
import BlockUi from 'react-block-ui';
import { Modal, Spinner, Button } from 'react-bootstrap';
// ICONS
import { HiChevronRight } from 'react-icons/hi';
import { BiSearchAlt2 } from 'react-icons/bi';
// CUSTOM COMPONENTS
import CustomBlock from '../../../components/CustomBlock';
import Agenda from '../../../components/Agenda';
import { FormControlLabel } from '@material-ui/core';
import { Switch } from '@material-ui/core';
import JobScheduleInfoBlock from '../../services/jobs/_partials/JobScheduleInfoBlock';
//SERVICES AND HELPERS
import * as dashboardService from '../../../../services/dashboard/dashboardService';
import * as alert from '../../../../redux/alertSnackBarRedux';
import * as confirmModal from '../../../../redux/confirmModalRedux';
import constants from '../../../../constants/constants';

const locales = {
  'en-US': require('date-fns/locale/en-US'),
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

const PHONE_CALENDAR_QUERY = '(max-width: 575.98px)';
const CALENDAR_VIEWS = ['month', 'week', 'day', 'agenda'];

const useMediaQuery = (query) => {
  const getMatches = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches;

  const [matches, setMatches] = useState(getMatches);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(query);
    const handleChange = (event) => setMatches(event.matches);

    setMatches(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (typeof mediaQuery.removeEventListener === 'function') {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, [query]);

  return matches;
};

const DashboardPhoneAgendaEvent = ({ event, onSelectEvent }) => (
  <button type='button' className='aes-dashboard-calendar__agenda-event' onClick={() => onSelectEvent(event)}>
    <span className='aes-dashboard-calendar__agenda-title'>{event.title}</span>
    {event.description && <span className='aes-dashboard-calendar__agenda-description'>{event.description}</span>}
  </button>
);

const JobCalendarBlock = (props) => {
  const { showAlert, showConfirmModal, hideConfirmModal, setProcessing, auth } = props;

  const isPhoneCalendar = useMediaQuery(PHONE_CALENDAR_QUERY);
  const [showOnlyMy, setShowOnlyMy] = useState(true);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshCalendarBlock, setRefreshCalendarBlock] = useState(false);
  const [scheduleInfo, setScheduleInfo] = useState(null);
  const [groupedSchedule, setGroupedSchedule] = useState([]);
  const [switchMode, setSwitchMode] = useState(false);
  const [groupedModalOpen, setGroupedModalOpen] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState(isPhoneCalendar ? 'agenda' : 'month');

  useEffect(() => {
    getSchedule();
  }, [showOnlyMy, switchMode, refreshCalendarBlock]);

  useEffect(() => {
    setCalendarView(isPhoneCalendar ? 'agenda' : 'month');
  }, [isPhoneCalendar]);

  const closeGroupedModal = () => {
    setGroupedModalOpen(false);
    setGroupedSchedule([]);
  };

  const getSchedule = () => {
    setLoading(true);
    dashboardService
      .getScheduleForDashboard(showOnlyMy, switchMode)
      .then((res) => {
        if (showOnlyMy) {
          setData(
            res.map((x) => {
              return {
                ...x,
                start: new Date(x.start),
                end: new Date(x.end),
                title: '#' + x.projectNumber + '- (' + x.name + ')',
                description: `# ${x.projectNumber}: ${x.additionalNotes ? x.additionalNotes : 'N/A'}`,
                switchMode: switchMode,
                refresh: refreshCalendarBlock,
                userFullName: '',
              };
            }),
          );
        } else {
          !switchMode
            ? setData(
                res.map((x) => {
                  return {
                    ...x,
                    start: new Date(x.start),
                    end: new Date(x.end),
                    title: '#' + x.projectNumber + ' - (' + x.noOfRecords + ') scheduled',
                    description: `# ${x.projectNumber}: ${x.additionalNotes ? x.additionalNotes : 'N/A'}`,
                    switchMode: switchMode,
                    refresh: refreshCalendarBlock,
                    userFullName: '',
                  };
                }),
              )
            : setData(
                res.map((x) => {
                  return {
                    ...x,
                    start: new Date(x.start),
                    end: new Date(x.end),
                    title: '#' + x.projectNumber + ' - (' + x.noOfRecords + ') scheduled',
                    description: `# ${x.projectNumber}: ${x.additionalNotes ? x.additionalNotes : 'N/A'} -- ${x.userFullName}`,
                    imageUrl: x.imageUrl,
                    userId: x.userId,
                    switchMode: switchMode,
                    refresh: refreshCalendarBlock,
                    userFullName: x.userFullName,
                  };
                }),
              );
        }
      })
      .catch((ex) => {})
      .finally(() => setLoading(false));
  };

  const handleEventChange = (event) => {
    if (showOnlyMy) {
      setScheduleInfo(event);
    } else {
      openGroupedEvent(event);
    }
  };

  const openGroupedEvent = (event) => {
    setLoading(true);
    setGroupedModalOpen(true);
    dashboardService
      .getJobScheduleForGroup(event)
      .then((res) => {
        setGroupedSchedule(res);
      })
      .catch((ex) => {})
      .finally(() => {
        setLoading(false);
      });
  };

  const columns = [
    {
      name: 'name',
      label: 'User',
      options: {
        filterType: 'multiselect',
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          return (
            <div className='tc-user-block'>
              <div
                className='img-block background-image-container'
                style={{
                  backgroundImage: `url(${
                    groupedSchedule[dataIndex].imageUrl
                      ? constants.API_URL + groupedSchedule[dataIndex].imageUrl
                      : '/media/users/default.jpg'
                  })`,
                }}
              ></div>

              <div className='user-info-block'>
                <Link
                  to={'/management/user-profile/' + groupedSchedule[dataIndex].userId}
                  className='text-dark font-weight-bolder text-hover-primary mb-1 font-size-lg'
                >
                  {groupedSchedule[dataIndex].name}
                </Link>
              </div>
            </div>
          );
        },
      },
    },
    {
      name: 'projectNumber',
      label: 'Project#',
      options: {
        filter: true,
        filterType: 'multiselect',
        sort: true,
        customBodyRenderLite: (dataIndex) => (
          <Link to={'/services/manage-job/schedule/' + groupedSchedule[dataIndex].jobId}>#{groupedSchedule[dataIndex].projectNumber}</Link>
        ),
      },
    },
    {
      name: 'location',
      label: 'Job Location',
      options: {
        filter: true,
        filterType: 'multiselect',
        sort: true,
      },
    },
    {
      name: 'start',
      label: 'Start Date/Time',
      options: {
        filter: true,
        filterType: 'multiselect',
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          return <p>{moment(groupedSchedule[dataIndex].start).format('HH:mm, MM/DD/YYYY')}</p>;
        },
      },
    },
    {
      name: 'end',
      label: 'End Date/Time',
      options: {
        filter: true,
        filterType: 'multiselect',
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          return <p>{moment(groupedSchedule[dataIndex].end).format('HH:mm, MM/DD/YYYY')}</p>;
        },
      },
    },
  ];

  return (
    <>
      <BlockUi tag='div' blocking={loading}>
        <CustomBlock className='content-container--card-style large-card h-100 aes-dashboard-calendar'>
          <Modal centered size='lg' show={groupedModalOpen} onHide={closeGroupedModal}>
            <Modal.Header closeButton>
              <Modal.Title>Selected Schedules</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <MUIDataTable
                data={groupedSchedule}
                columns={columns}
                options={{
                  rowsPerPage: 25,
                  rowsPerPageOptions: [10, 15, 20, 25, 30, 35, 40, 45, 50, 100],
                  filter: false,
                  selectableRows: 'none',
                  downloadOptions: {
                    filename: 'job_schedule.csv',
                    filterOptions: {
                      useDisplayedColumnsOnly: true,
                      useDisplayedRowsOnly: true,
                    },
                  },
                  textLabels: {
                    body: {
                      noMatch: loading ? <Spinner animation='border' size='lg' variant='info' /> : 'Sorry, no matching records found.',
                    },
                  },
                }}
              />
            </Modal.Body>
            <Modal.Footer>
              <Button variant='secondary' onClick={closeGroupedModal}>
                Close
              </Button>
            </Modal.Footer>
          </Modal>
          <JobScheduleInfoBlock
            showAlert={showAlert}
            scheduleInfo={scheduleInfo}
            setScheduleInfo={setScheduleInfo}
            showConfirmModal={showConfirmModal}
            hideConfirmModal={hideConfirmModal}
            setProcessing={setProcessing}
            fetchJobSchedules={getSchedule}
            fromDashboard={true}
          />
          <CustomBlock className='aes-dashboard-calendar-block'>
            <CustomBlock className='aes-dashboard-calendar-filters' style={{ display: 'flex', justifyContent: 'space-between' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showOnlyMy}
                    onChange={() => {
                      setShowOnlyMy(!showOnlyMy);
                    }}
                    value='checkedB'
                    color='primary'
                  />
                }
                label='Show Only My Calendar'
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={switchMode}
                    onChange={() => {
                      setSwitchMode(!switchMode);
                    }}
                    value='checkedB'
                    color='primary'
                  />
                }
                label='Switch To User Mode'
              />
            </CustomBlock>

            <Calendar
              className={`aes-dashboard-calendar${!loading && data.length === 0 ? ' aes-dashboard-calendar--empty' : ''}`}
              popup
              localizer={localizer}
              events={data}
              startAccessor='start'
              endAccessor='end'
              date={calendarDate}
              view={calendarView}
              views={CALENDAR_VIEWS}
              length={isPhoneCalendar ? 1 : 30}
              onNavigate={(date) => setCalendarDate(date)}
              onView={(view) => {
                if (CALENDAR_VIEWS.includes(view)) {
                  setCalendarView(view);
                }
              }}
              onSelectEvent={(e) => handleEventChange(e)}
              eventPropGetter={(event, start, end, isSelected) => {
                return {
                  style: {
                    backgroundColor: getBackgroundColorByType(event.projectName),
                  },
                };
              }}
              components={{
                agenda: {
                  event: isPhoneCalendar
                    ? (agendaProps) => <DashboardPhoneAgendaEvent {...agendaProps} onSelectEvent={handleEventChange} />
                    : Agenda,
                },
              }}
            />
          </CustomBlock>
        </CustomBlock>
      </BlockUi>
    </>
  );
};

const getBackgroundColorByType = (type) => {
  switch (type) {
    case 'Professional':
      return '#86C5D8';
    case 'Remediation':
      return '#ffbf00';
    case 'Restoration':
      return '#1A8A54';
    default:
      return '#FC5C14';
  }
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, {
  ...alert.actions,
  ...confirmModal.actions,
})(JobCalendarBlock);
